import { useMemo } from 'react';
import match from 'autosuggest-highlight/match';
import parse from 'autosuggest-highlight/parse';

import Box from '@mui/material/Box';
import { alpha } from '@mui/material/styles';

// ----------------------------------------------------------------------

interface Props {
  text: string;
  /** Terms to highlight. When empty the text renders unchanged. */
  terms?: string[];
}

/** Merge possibly-overlapping/unsorted [start, end] ranges so `parse` gets clean, ordered chunks. */
const mergeRanges = (ranges: [number, number][]): [number, number][] => {
  if (ranges.length <= 1) return ranges;
  const sorted = [...ranges].sort((a, b) => a[0] - b[0]);
  const merged: [number, number][] = [sorted[0]];
  for (let i = 1; i < sorted.length; i += 1) {
    const last = merged[merged.length - 1];
    const [start, end] = sorted[i];
    if (start <= last[1]) last[1] = Math.max(last[1], end);
    else merged.push([start, end]);
  }
  return merged;
};

/**
 * Renders text with search terms visually highlighted (spec items 14/18, JT1-1537). Uses the same
 * `autosuggest-highlight` matcher the global search bar already relies on, wrapping each hit in a
 * theme-aware <mark>. Shared by the record-panel resume body and the profile Resume tab so a search
 * highlights identically on both. With no terms it renders the text unchanged.
 */
export default function HighlightedText({ text, terms }: Props) {
  // Memoised because a long resume with every occurrence matched is not free to re-scan on each render.
  const parts = useMemo(() => {
    const active = (terms ?? []).map((term) => term.trim()).filter(Boolean);
    if (!active.length || !text) return null;

    // `match` splits each term into words and, without `findAllOccurrences`, stops at the first hit of
    // each word — so a word repeated down the resume was only highlighted once. Merging the ranges
    // keeps overlapping hits from different terms as a single mark.
    const ranges = mergeRanges(
      active.flatMap((term) => match(text, term, { insideWords: true, findAllOccurrences: true }) as [number, number][])
    );
    return parse(text, ranges);
  }, [text, terms]);

  if (!parts) return <>{text}</>;

  return (
    <>
      {parts.map((part, index) =>
        part.highlight ? (
          // eslint-disable-next-line react/no-array-index-key
          <Box
            key={index}
            component="mark"
            sx={{
              px: 0.25,
              borderRadius: 0.5,
              color: 'inherit',
              bgcolor: (theme) => alpha(theme.palette.warning.main, 0.4),
            }}
          >
            {part.text}
          </Box>
        ) : (
          // eslint-disable-next-line react/no-array-index-key
          <span key={index}>{part.text}</span>
        )
      )}
    </>
  );
}
